import React from 'react'

import { cn } from './cn'
import { ImageBase } from './Image'

// DOM mirrors Semantic UI React's Item (views/Item/Item.js) and its children
// (ItemGroup, ItemImage, ItemContent, ItemHeader, ItemMeta, ItemDescription, ItemExtra).
// Children only: no shorthand props are used by the app. Styles are scoped to the item
// and its descendants, because Semantic scoped them to `.ui.items > .item`.

export type ItemProps = React.HTMLAttributes<HTMLDivElement> & {
  as?: React.ElementType
  children?: React.ReactNode
}

export type ItemGroupProps = React.HTMLAttributes<HTMLDivElement> & {
  as?: React.ElementType
  children?: React.ReactNode
  divided?: boolean
  relaxed?: boolean
}

export type ItemImageProps = Omit<
  React.HTMLAttributes<HTMLElement>,
  'color'
> & {
  alt?: string
  children?: React.ReactNode
  size?: 'tiny'
  src?: string
}

export type ItemContentProps = React.HTMLAttributes<HTMLDivElement> & {
  as?: React.ElementType
  children?: React.ReactNode
  verticalAlign?: 'middle'
}

export type ItemTextProps = React.HTMLAttributes<HTMLElement> & {
  as?: React.ElementType
  children?: React.ReactNode
}

export type ItemHeaderProps = ItemTextProps & {
  href?: string
}

// Item root: `.ui.items > .item`. Semantic gives these to the item from its parent
// group, so they live here. `after:` is the clearfix. The child rules below target the
// Semantic children: image, content, header, description, meta and extra.
const ITEM_BASE = [
  'flex w-full min-h-0 p-0 bg-transparent border-0 rounded-none shadow-none text-[1em]',
  '[transition:box-shadow_0.1s_ease] my-[1em] mx-0',
  "after:block after:h-0 after:clear-both after:invisible after:overflow-hidden after:content-['']",
  // `.ui.items > .item > .image`: a flex item that keeps its size.
  '[&>.image]:relative [&>.image]:block [&>.image]:flex-[0_0_auto] [&>.image]:float-none [&>.image]:m-0 [&>.image]:p-0',
  '[&>.image>img]:block [&>.image>img]:w-full [&>.image>img]:h-auto [&>.image>img]:rounded-[0.125rem] [&>.image>img]:border-0',
  '[&>.image:only-child>img]:rounded-none',
  // `.ui.items > .item > .content`: flex grows, no box.
  '[&>.content]:block [&>.content]:flex-[1_1_auto] [&>.content]:m-0 [&>.content]:p-0 [&>.content]:text-[1em] [&>.content]:shadow-none [&>.content]:border-0 [&>.content]:rounded-none',
  // `.image + .content`: indented after the image; `.middle` is verticalAlign="middle".
  '[&>.image+.content]:min-w-0 [&>.image+.content]:w-auto [&>.image+.content]:block [&>.image+.content]:ml-0 [&>.image+.content]:pl-[1.5em]',
  '[&>.image+.content.middle]:self-center',
  // `.ui.items > .item > .content > .header` and `a.header`.
  '[&>.content>.header]:inline-block [&>.content>.header]:mt-[-0.21425em] [&>.content>.header]:mr-0 [&>.content>.header]:mb-0 [&>.content>.header]:ml-0 [&>.content>.header]:font-sans [&>.content>.header]:font-bold [&>.content>.header]:text-[rgba(0,0,0,0.85)] [&>.content>.header:not(.ui)]:text-[1.28571429em]',
  '[&>.content>a.header]:text-[rgba(0,0,0,0.85)] [&>.content>a.header:hover]:text-[#1e70bf]',
  // `.ui.items > .item > .content > .description`.
  '[&>.content>.description]:mt-[0.6em] [&>.content>.description]:text-[1em] [&>.content>.description]:leading-[1.4285em] [&>.content>.description]:text-[rgba(0,0,0,0.87)]',
  '[&>.content_p]:m-[0_0_0.5em] [&>.content_p:last-child]:mb-0',
  // `.ui.items > .item .meta`: font size before leading.
  '[&_.meta]:m-[0.5em_0_0.5em] [&_.meta]:text-[1em] [&_.meta]:leading-[1em] [&_.meta]:text-[rgba(0,0,0,0.6)] [&_.meta_*]:mr-[0.3em] [&_.meta_:last-child]:mr-0 [&_.meta>a:not(.ui)]:text-[rgba(0,0,0,0.4)] [&_.meta>a:not(.ui):hover]:text-[rgba(0,0,0,0.87)]',
  // `.ui.items > .item .extra`.
  '[&_.extra]:block [&_.extra]:relative [&_.extra]:m-[0.5rem_0_0] [&_.extra]:w-full [&_.extra]:p-0 [&_.extra]:text-[rgba(0,0,0,0.4)] [&_.extra]:shadow-none [&_.extra]:border-0 [&_.extra]:[transition:color_0.1s_ease]',
  '[&_.extra>*]:m-[0.25rem_0.5rem_0.25rem_0]',
].join(' ')

// Item.Group: `.ui.items` plus the `divided` and `relaxed` rules that apply to its items.
const GROUP_BASE = 'mt-[1.5em] mx-0 mb-[1.5em] first:!mt-0 last:!mb-0'
const GROUP_RELAXED = '[&>.item]:my-[1.5em] [&>.item]:mx-0'
const GROUP_DIVIDED =
  '[&>.item]:m-0 [&>.item]:p-[1em_0] [&>.item]:border-t-[1px] [&>.item]:border-solid [&>.item]:border-t-[rgba(34,36,38,0.15)] [&>.item:first-child]:border-t-0 [&>.item:first-child]:!mt-0 [&>.item:first-child]:!pt-0 [&>.item:last-child]:!mb-0 [&>.item:last-child]:!pb-0'
const GROUP_DIVIDED_RELAXED =
  '[&>.item]:m-0 [&>.item]:p-[1.5em_0] [&>.item]:border-t-[1px] [&>.item]:border-solid [&>.item]:border-t-[rgba(34,36,38,0.15)] [&>.item:first-child]:border-t-0 [&>.item:first-child]:!mt-0 [&>.item:first-child]:!pt-0 [&>.item:last-child]:!mb-0 [&>.item:last-child]:!pb-0'

function ItemRoot({ as, children, className, ...rest }: ItemProps) {
  const Tag: React.ElementType = as ?? 'div'
  return (
    <Tag {...rest} className={cn('item', ITEM_BASE, className)}>
      {children}
    </Tag>
  )
}

// `Item.Image` renders an Image: `ui tiny image` (size given) wrapped in a div, as
// Semantic's ItemImage does.
function ItemImage({ children, size, ...rest }: ItemImageProps) {
  return (
    <ImageBase {...rest} size={size} ui={!!size} wrapped>
      {children}
    </ImageBase>
  )
}

function ItemGroup({
  as,
  children,
  className,
  divided,
  relaxed,
  ...rest
}: ItemGroupProps) {
  const Tag: React.ElementType = as ?? 'div'
  const classes = cn(
    'ui',
    divided && 'divided',
    relaxed && 'relaxed',
    'items',
    'ui-items',
    GROUP_BASE,
    divided ? (relaxed ? GROUP_DIVIDED_RELAXED : GROUP_DIVIDED) : undefined,
    !divided && relaxed && GROUP_RELAXED,
    className,
  )
  return (
    <Tag {...rest} className={classes}>
      {children}
    </Tag>
  )
}

function ItemContent({
  as,
  children,
  className,
  verticalAlign,
  ...rest
}: ItemContentProps) {
  const Tag: React.ElementType = as ?? 'div'
  return (
    <Tag
      {...rest}
      className={cn(
        verticalAlign === 'middle' && 'middle aligned',
        'content',
        className,
      )}
    >
      {children}
    </Tag>
  )
}

// Item.Header: `.header`, or `a.header` when `as="a"`. Forwards its ref, as Semantic's
// ItemHeader does, for the Link and Ref wrappers in components/ForwardedHeader.jsx.
const ItemHeader = React.forwardRef<HTMLElement, ItemHeaderProps>(
  function ItemHeader({ as, children, className, ...rest }, ref) {
    const Tag: React.ElementType = as ?? 'div'
    return (
      <Tag {...rest} ref={ref} className={cn('header', className)}>
        {children}
      </Tag>
    )
  },
)

function textPart(marker: string) {
  return function ItemText({
    as,
    children,
    className,
    ...rest
  }: ItemTextProps) {
    const Tag: React.ElementType = as ?? 'div'
    return (
      <Tag {...rest} className={cn(marker, className)}>
        {children}
      </Tag>
    )
  }
}

const ItemMeta = textPart('meta')
const ItemDescription = textPart('description')
const ItemExtra = textPart('extra')

export const Item = Object.assign(ItemRoot, {
  Content: ItemContent,
  Description: ItemDescription,
  Extra: ItemExtra,
  Group: ItemGroup,
  Header: ItemHeader,
  Image: ItemImage,
  Meta: ItemMeta,
})
